/**
 * Engineered SFX, synthesized at runtime. No asset files.
 * Each call schedules short envelopes on fresh oscillator/noise sources.
 * Voices are layered to give each event a fuller, more cinematic feel.
 */
export class Sfx {
  private ctx?: AudioContext;
  private master?: GainNode;
  private mutedFlag = false;
  private lastSurgeAt = 0;

  init() {
    if (this.ctx) return;
    try { this.mutedFlag = localStorage.getItem('nodefall.muted') === '1'; } catch { /* ignore */ }
    const Ctx = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
    if (!Ctx) return;
    this.ctx = new Ctx();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.mutedFlag ? 0 : 0.45;
    this.master.connect(this.ctx.destination);
  }

  muted(): boolean {
    return this.mutedFlag;
  }

  private persistMute() {
    try { localStorage.setItem('nodefall.muted', this.mutedFlag ? '1' : '0'); } catch { /* ignore */ }
  }

  toggleMute(): boolean {
    this.mutedFlag = !this.mutedFlag;
    if (this.master) this.master.gain.value = this.mutedFlag ? 0 : 0.45;
    this.persistMute();
    return this.mutedFlag;
  }

  setMuted(on: boolean): boolean {
    if (this.mutedFlag !== on) return this.toggleMute();
    return this.mutedFlag;
  }

  private envelope(node: AudioNode, attack: number, release: number, peak = 1, sustain = 0): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(peak, ctx.currentTime + attack);
    if (sustain > 0) {
      g.gain.linearRampToValueAtTime(peak * 0.6, ctx.currentTime + attack + sustain);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + attack + sustain + release);
    } else {
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + attack + release);
    }
    node.connect(g);
    g.connect(this.master!);
    return g;
  }

  private osc(type: OscillatorType, freq: number, attack: number, release: number, peak = 1, freqEnd?: number, sustain = 0) {
    if (!this.ctx || this.mutedFlag) return;
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, this.ctx.currentTime);
    if (freqEnd !== undefined) {
      o.frequency.exponentialRampToValueAtTime(Math.max(1, freqEnd), this.ctx.currentTime + attack + sustain + release);
    }
    this.envelope(o, attack, release, peak, sustain);
    o.start();
    o.stop(this.ctx.currentTime + attack + sustain + release + 0.05);
  }

  private noise(attack: number, release: number, peak = 0.4, filterHz?: number, q = 1.2) {
    if (!this.ctx || this.mutedFlag) return;
    const ctx = this.ctx;
    const bufSize = Math.floor(ctx.sampleRate * (attack + release));
    const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    let node: AudioNode = src;
    if (filterHz) {
      const f = ctx.createBiquadFilter();
      f.type = 'bandpass';
      f.frequency.value = filterHz;
      f.Q.value = q;
      src.connect(f);
      node = f;
    }
    this.envelope(node, attack, release, peak);
    src.start();
    src.stop(ctx.currentTime + attack + release + 0.05);
  }

  /** Initial breach tap: sub-bass thump + bright digital click + air. */
  tap() {
    this.osc('sine', 110, 0.005, 0.22, 0.95, 38);
    this.osc('square', 1600, 0.001, 0.04, 0.16, 800);
    this.noise(0.001, 0.06, 0.34, 3200);
  }

  /** Pulse traveling on a wire — short throttled tick. */
  surge() {
    const now = performance.now();
    if (now - this.lastSurgeAt < 32) return;
    this.lastSurgeAt = now;
    this.osc('square', 1500, 0.001, 0.05, 0.18, 950);
  }

  /** A node collapsing — layered crack + sub thud + brief hiss. */
  collapse() {
    this.osc('triangle', 320, 0.002, 0.14, 0.7, 90);
    this.osc('sine', 70, 0.005, 0.20, 0.6, 28);
    this.noise(0.001, 0.10, 0.28, 1600, 0.9);
  }

  /** Protected overload — dissonant two-tone klaxon. */
  alarm() {
    this.osc('sawtooth', 210, 0.008, 0.30, 0.5);
    this.osc('sawtooth', 224, 0.008, 0.30, 0.5);
    this.osc('square', 880, 0.002, 0.12, 0.18);
  }

  /** Relay opens a wire / shield drops — ascending blip with shimmer. */
  unlock() {
    this.osc('triangle', 540, 0.003, 0.22, 0.45, 980);
    this.osc('sine', 1300, 0.001, 0.12, 0.18, 1900);
  }

  /** Win: descending tonic + dominant sub + crystalline shimmer. */
  win() {
    // sub sweep down
    this.osc('sine', 80, 0.01, 0.85, 0.85, 34);
    // major chord stab (G + B + D-ish)
    this.osc('triangle', 392, 0.005, 0.55, 0.4);
    this.osc('triangle', 494, 0.008, 0.55, 0.32);
    this.osc('triangle', 587, 0.010, 0.55, 0.28);
    // bright shimmer
    setTimeout(() => {
      this.osc('sine', 1568, 0.005, 0.40, 0.22, 2200);
    }, 90);
  }

  /** Lose: short noise blast + slumping bass. */
  lose() {
    this.osc('sawtooth', 200, 0.005, 0.6, 0.55, 50);
    this.osc('sine', 60, 0.02, 0.7, 0.4, 25);
    this.noise(0.001, 0.30, 0.18, 600);
  }

  /** Soft UI tick — for menu / button feedback. */
  ui() {
    this.osc('triangle', 880, 0.002, 0.05, 0.18, 660);
  }
}
